import history from "@rista/core/utils/navigation";
import { isKioskModeEnabled } from '@rista/core/kiosk/config';
// ONE definition of "inside the kiosk", shared with the render-time half of the door
// (commonComponents/KioskLock). Two predicates that could disagree is one too many.
import { isKioskPath } from '@rista/core/kiosk/navigation/routes';
import { ROUTE_CONSTANTS_MAP } from 'utils/routeConstants';

/**
 * The browser-history singleton now lives in `@rista/core/utils/navigation`
 * (`index.desktop.ts` on web). This file no longer creates it — it IMPORTS the one instance,
 * installs the kiosk guard on it, and re-exports it.
 *
 * ⚠️ Keep re-exporting the SAME instance rather than calling `createBrowserHistory()` here.
 * `CustomRouter.js` wraps react-router's `<Router>` around this object, and core's
 * `redirectToLicenseLandingPage` pushes onto it; two instances would silently split navigation
 * in half.
 *
 * ⚠️ Imported (not `export { default } from`) because the body below mutates the instance — an
 * `export ... from` re-export creates no local binding.
 */

/**
 * KIOSK MODE IS A ONE-WAY DOOR.
 *
 * While it is on, nothing may navigate out of `/kiosk` — not a saga, not the API
 * interceptor, not a notification, not a stale deep link. A customer must never reach
 * another screen, and navigation in this app is fired from outside React all over the
 * place, so the guard lives at the single object every one of those callers shares
 * rather than in any one of them.
 *
 * Leaving is not blocked, it is SEQUENCED: the exit control sets kiosk mode to false
 * first (see Containers/Kiosk), after which this is an ordinary passthrough. With the
 * mode still on, an attempted exit is a no-op rather than a redirect, because a silent
 * bounce is easier to reason about than two navigations racing.
 *
 * ⚠️ **THIS IS ONLY HALF THE DOOR, and the half it is not covering is the one a customer
 * can reach with a finger.** Wrapping `push`/`replace` cannot stop a browser BACK: history
 * v5 applies a POP and only then notifies listeners, so `history.back()` walks out of
 * `/kiosk` no matter what this file does (measured, not assumed). Cold boots and deep links
 * get past it for the same reason. `commonComponents/KioskLock` is the render-time half
 * that closes those; both share `isKioskPath` so they cannot disagree about what "inside
 * the kiosk" means.
 *
 * See docs/KIOSK_ARCHITECTURE.md §13.10.
 *
 * 🔴 **The guard is installed when THIS module evaluates, and the object it guards is now
 * created elsewhere.** Before the DP-8057 merge this file created the instance, so holding it
 * implied the guard was on. Now a core module could in principle import
 * `@rista/core/utils/navigation` and push before this file has been evaluated. In practice it
 * cannot: the only core-side holder is `helpers/commonHelpers.redirectToLicenseLandingPage`,
 * which runs from a saga long after boot, while `index.tsx -> CustomRouter.js` imports this
 * file during module evaluation. **If a core module ever needs to navigate at import time, the
 * guard must move into `@rista/core/utils/navigation/index.desktop.ts` alongside the singleton.**
 */
const guard = (method) => {
  const original = history[method].bind(history);
  history[method] = (to, ...rest) => {
    if (isKioskModeEnabled() && !isKioskPath(to, ROUTE_CONSTANTS_MAP.KIOSK_ROUTE)) {
      return undefined;
    }
    return original(to, ...rest);
  };
};

guard('push');
guard('replace');

export default history;
